<template>
  <LoadingDiv v-if="!isReady" />
  <div v-else class="plan-manager">
    <header class="plan-header">
      <div><span>Coverage configuration</span><h1>Plans</h1><p>Manage private and social plans, premiums, and benefit schedules.</p></div>
      <button type="button" class="plan-outline-button" @click="next = !next"><X v-if="next" :size="16" /><Plus v-else :size="16" />{{ next ? "Cancel" : "Add plan" }}</button>
    </header>

    <PolicyEditDiv v-if="!next && editPolicy" :policy="editPolicy" @complete="handleUpdateComplete" />

    <template v-else-if="!next">
      <section v-if="plans.length" class="plan-grid">
        <article v-for="plan in plans" :key="plan.id" class="plan-card" :class="{ 'plan-card-suspended': plan.status === 's' }">
          <div class="plan-card-heading">
            <div><span class="plan-badge">{{ schemeType(plan) === "p" ? "Private" : "Social" }}</span><h2>{{ plan.name }}</h2><p>{{ plan.description }}</p></div>
            <div class="plan-card-actions">
              <button type="button" class="plan-icon-button" title="Edit plan" @click="handleEdit(plan)"><Pencil :size="16" /></button>
              <button v-if="schemeType(plan) === 'p'" type="button" class="plan-icon-button" :title="plan.status === 'a' ? 'Suspend plan' : 'Activate plan'" :disabled="isLoading" @click="handleStatus(plan)"><CirclePause v-if="plan.status === 'a'" :size="16" /><CirclePlay v-else :size="16" /></button>
            </div>
          </div>
          <div class="plan-premiums"><div><span>Individual premium</span><strong>₦{{ toPremium(plan.premiums, "i") }}</strong></div><div><span>Family premium</span><strong>₦{{ toPremium(plan.premiums, "f") }}</strong></div></div>
          <button type="button" class="plan-benefit-toggle" @click="showBenefits(plan)"><span>{{ benefitCount(plan) }} benefit line items</span><ChevronUp v-if="showBenefit === plan.id" :size="16" /><ChevronDown v-else :size="16" /></button>
          <div v-if="showBenefit === plan.id" class="plan-benefits">
            <div v-for="(benefit, index) in (plan.benefits || []).slice(0, 10)" :key="benefit.id" class="plan-benefit-row"><span>{{ index + 1 }}</span><strong>{{ benefit.item }}</strong><p>{{ benefit.description }}</p></div>
            <p v-if="benefitCount(plan) > 10" class="plan-benefit-more">And {{ benefitCount(plan) - 10 }} more benefits.</p>
          </div>
        </article>
      </section>
      <div v-else class="plan-empty">No plans have been added.</div>
      <footer class="plan-page-actions"><button type="button" class="plan-primary-button" @click="handleNext">Continue <ArrowRight :size="16" /></button></footer>
    </template>

    <form v-else class="plan-form" @submit.prevent="handleAdd">
      <div class="plan-form-heading"><FileSpreadsheet :size="20" /><div><h2>Add shelf plan</h2><p>Set plan premiums and upload its Excel benefit schedule.</p></div></div>
      <div v-if="error" class="plan-error-box">{{ uploadError || "Complete all required fields before adding the plan." }}</div>
      <div class="plan-form-grid">
        <label class="plan-field plan-field-wide"><span>Policy name</span><input v-model="policyName" type="text" required /></label>
        <label class="plan-field"><span>Individual premium</span><div class="plan-money-input"><span>₦</span><input v-model="premiumIndividual" type="number" min="0" required /></div></label>
        <label class="plan-field"><span>Family premium</span><div class="plan-money-input"><span>₦</span><input v-model="premiumFamily" type="number" min="0" /></div></label>
        <label class="plan-field plan-file-field plan-field-wide"><span>Benefit schedule</span><input type="file" accept=".xlsx,.xls" :disabled="isSubmitting" @change="handleFile" /><span class="plan-file-control"><Upload :size="16" />{{ inputFile || "Choose Excel file" }}</span><small v-if="fileError">{{ fileError }}</small></label>
      </div>
      <footer class="plan-form-actions"><button type="button" class="plan-outline-button" :disabled="isSubmitting" @click="handleBack"><X :size="16" />Cancel</button><button class="plan-primary-button" :disabled="isSubmitting"><Plus :size="16" />{{ isSubmitting ? `Adding (${uploadProgress})` : "Add plan" }}</button></footer>
    </form>
  </div>
</template>
<script>
import { ref, computed, watchEffect, onMounted } from "vue";
import { ArrowRight, ChevronDown, ChevronUp, CirclePause, CirclePlay, FileSpreadsheet, Pencil, Plus, Upload, X } from "@lucide/vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
// import postItem from "@/composables/postItem";
import useStorage from "@/composables/useStorage";
import putItem from "@/composables/putItem";
import PolicyEditDiv from "@/components/client/onboard/PolicyEditOnboardComponent.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { adminAPI } from '@/laravel/config';

export default {
  name: "PlanSetupComponent",
  components: { ArrowRight, ChevronDown, ChevronUp, CirclePause, CirclePlay, FileSpreadsheet, LoadingDiv, Pencil, Plus, PolicyEditDiv, Upload, X },
  emits: ["next"],
  setup(props, context) {
    const response = ref(null);
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const next = ref(false);
    const policy = ref([]);
    const policyName = ref(null);
    const premiumIndividual = ref(null);
    const premiumFamily = ref(null);
    const showBenefit = ref(null);
    const editPolicy = ref(null);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // toMoney
    const toMoney = (val) => {
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    const getPremium = (premiums, type) => {
      if (!premiums || !premiums.length) {
        return null;
      }

      return premiums.find((x) => x.type === type) || null;
    };

    // to premium
    const toPremium = (premiums, type) => {
      const selectedPremium = getPremium(premiums, type);

      if (!selectedPremium || selectedPremium.amount === null || selectedPremium.amount === undefined) {
        return "Not set";
      }

      return toMoney(selectedPremium.amount);
    }

    const schemeType = (plan) => {
      return plan && plan.scheme ? plan.scheme.type : null;
    };

    const benefitCount = (plan) => {
      return plan && plan.benefits ? plan.benefits.length : 0;
    };

    // show benefits
    const showBenefits = (val) => {
      if (val.id != showBenefit.value) {
        showBenefit.value = val.id;
      } else {
        showBenefit.value = null;
      }
    };

    // start
    onMounted(() => {
      // get items
      loadPlans();
    });

    // handle plans loader
    const loadPlans = () => {
      // loasder on
      isReady.value = false;
      // start
      (async () => {
        const { res, err } = await getItems("plan/setup");

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
      })();
    }

    // edit policy
    const handleEdit = (val) => {
      if (val) {
        // sort
        let newVal = { ...val };
        const individualPremium = getPremium(val.premiums, "i");
        const familyPremium = getPremium(val.premiums, "f");
        newVal.premiumIndividual = individualPremium ? individualPremium.amount : 0;
        newVal.premiumFamily = familyPremium ? familyPremium.amount : 0;
        // load
        editPolicy.value = newVal;
      }
    };

    // edit status
    const handleStatus = async (val) => {

      // only private plans can be suspended here
      if (schemeType(val) === 'p') {

        // start
        isLoading.value = true;

        // value
        let newStatus;
        let planId = val.id;

        // new status
        if (val.status == "a") {
          newStatus = "s"; // update status
        } else if (val.status == "s") {
          newStatus = "a"; // update status
        }

        // update
        let newVal = {
          status: newStatus,
        };

        // post item
        const { res, err } = await putItem("plan", planId, newVal);

        if (res.value) {
          // reload plans
          loadPlans();
        } else {
          error.value = err.value;
        }

        // end
        isLoading.value = false;

      }

    };

    // handle update complete
    const handleUpdateComplete = (val) => {
      // release
      editPolicy.value = null;

      if (val.complete) {
        loadPlans();
      }
    };

    // handle add
    const handleReset = () => {
      // clear files
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      uploadProgress.value = "0%";
      uploadError.value = null;
      data.value = null;
      completed.value = null;
      // null all
      policyName.value = null;
      premiumIndividual.value = null;
      premiumFamily.value = null;
      // next
      next.value = false;
      // clear error
      error.value = false;
      // clear loading
      isLoading.value = false;
      // clear submitting
      isSubmitting.value = false;
      // isReady ???
    };

    const handleBack = () => {
      // restart
      handleReset();
    };

    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
    };

    // handle add
    const handleAdd = async () => {
      if (
        policyName.value &&
        premiumIndividual.value &&
        file.value  //  && premiumFamily.value not necessary
      ) {
        // set family 
        if (!premiumFamily.value) {
          premiumFamily.value = "0.00";
        }
        // Benefits
        // Start Uploading
        isSubmitting.value = true;

        //start
        uploadError.value = null;

        // Cloud Function
        const fx = "planLoader";

        // setup
        // new plan
        const plan = {
          scheme_id: null, // update this
          name: policyName.value,
          description: "Standard Shelf Plan",
        };

        // individual premium
        const individual = {
          plan_id: null, // update this on server
          amount: premiumIndividual.value,
          type: "i",
        };

        // family premium
        const family = {
          plan_id: null, // update this on server
          amount: premiumFamily.value,
          type: "f",
        };

        // scope
        const scope = 0; // default

        // Start Uploading
        // uploadProgress.value = "100%";

        // round up,
        const cloudInput = {
          plan: plan,
          individual: individual,
          family: family,
          scope: scope,
          action: "new",
          endPoint: `${adminAPI}/plan/benefits/bulk`,
        };

        // // upload
        uploadFile(file.value, fx, cloudInput);
      } else {
        error.value = true;
      }
    };

    // watch upload
    watchEffect(() => {
      if (completed.value) {
        // clear
        handleReset();
        // success
        // handleSuccess(data.value.succ.inserted_rows);
        handleSuccess();
      }
      if (uploadError.value) {
        // restart
        // handleReset();
        // stop
        error.value = uploadError.value;
        isSubmitting.value = false;
      }
    });

    // handle success
    const handleSuccess = () => {
      // update
      loadPlans();
    };

    // handle remove
    const handleRemove = (val) => {
      // filter delete
      policy.value = policy.value.filter((x) => x.id != val);
      // clean up
      handleReset();
    };

    // handle submit
    const handleNext = () => {
      // next
      // emit new parameters
      context.emit("next", {
        step: 3,
        isComplete: true,
      });
    };

    // output results (plans)
    const plans = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      isReady,
      isLoading,
      isSubmitting,
      error,
      handleStatus,
      handleEdit,
      handleNext,
      handleFile,
      handleAdd,
      handleBack,
      handleRemove,
      handleUpdateComplete,
      showBenefits,
      schemeType,
      benefitCount,
      toPremium,
      toMoney,
      showBenefit,
      editPolicy,
      uploadProgress,
      fileError,
      uploadError,
      file,
      inputFile,
      next,
      policyName,
      premiumIndividual,
      premiumFamily,
      plans,
    };
  },
};
</script>

<style scoped>
.plan-manager { display: grid; gap: 1rem; }
.plan-header { align-items: center; background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); display: flex; justify-content: space-between; padding: 1.35rem; }
.plan-header > div > span { color: var(--ui-primary); font-size: .7rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.plan-header h1 { color: var(--ui-foreground); font-family: var(--ui-font-display); font-size: 1.35rem; margin: .2rem 0; }
.plan-header p, .plan-card-heading p, .plan-form-heading p { color: var(--ui-muted-foreground); margin: 0; }
.plan-grid { display: grid; gap: 1rem; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.plan-card { background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); overflow: hidden; }
.plan-card-suspended { background: color-mix(in srgb, var(--ui-warning) 4%, var(--ui-card)); }
.plan-card-heading { align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between; padding: 1.2rem; }
.plan-card-heading h2 { color: var(--ui-foreground); font-size: 1.05rem; margin: .55rem 0 .2rem; }
.plan-badge { background: var(--ui-secondary); border: 1px solid var(--ui-border); border-radius: 999px; color: var(--ui-secondary-foreground); display: inline-flex; font-size: .68rem; font-weight: 650; padding: .25rem .5rem; }
.plan-card-actions { display: flex; gap: .4rem; }
.plan-icon-button { align-items: center; background: var(--ui-background); border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); cursor: pointer; display: flex; height: 2.25rem; justify-content: center; width: 2.25rem; }
.plan-icon-button:disabled { cursor: not-allowed; opacity: .5; }
.plan-premiums { border-top: 1px solid var(--ui-border); display: grid; grid-template-columns: repeat(2, 1fr); }
.plan-premiums > div { display: grid; gap: .25rem; padding: .9rem 1.2rem; }
.plan-premiums > div + div { border-left: 1px solid var(--ui-border); }
.plan-premiums span { color: var(--ui-muted-foreground); font-size: .7rem; }
.plan-premiums strong { color: var(--ui-foreground); font-size: .9rem; }
.plan-benefit-toggle { align-items: center; background: transparent; border: 0; border-top: 1px solid var(--ui-border); color: var(--ui-foreground); cursor: pointer; display: flex; font-size: .75rem; font-weight: 650; justify-content: space-between; padding: .9rem 1.2rem; width: 100%; }
.plan-benefits { border-top: 1px solid var(--ui-border); padding: .3rem 1.2rem 1rem; }
.plan-benefit-row { align-items: start; display: grid; gap: .65rem; grid-template-columns: 1.5rem minmax(7rem, .7fr) 1fr; padding: .7rem 0; }
.plan-benefit-row + .plan-benefit-row { border-top: 1px solid var(--ui-border); }
.plan-benefit-row span { align-items: center; background: var(--ui-secondary); border-radius: 999px; display: flex; font-size: .65rem; height: 1.5rem; justify-content: center; }
.plan-benefit-row strong { color: var(--ui-foreground); font-size: .75rem; }
.plan-benefit-row p, .plan-benefit-more { color: var(--ui-muted-foreground); font-size: .75rem; margin: 0; }
.plan-empty { background: var(--ui-card); border: 1px dashed var(--ui-border); border-radius: var(--ui-radius); color: var(--ui-muted-foreground); padding: 2rem; text-align: center; }
.plan-page-actions { display: flex; justify-content: flex-end; }
.plan-form { background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); overflow: hidden; }
.plan-form-heading { align-items: center; border-bottom: 1px solid var(--ui-border); display: flex; gap: .75rem; padding: 1.2rem; }
.plan-form-heading h2 { color: var(--ui-foreground); font-size: 1rem; margin: 0 0 .15rem; }
.plan-form-grid { display: grid; gap: 1rem; grid-template-columns: repeat(2, minmax(0, 1fr)); padding: 1.2rem; }
.plan-field { display: grid; gap: .45rem; position: relative; }
.plan-field-wide { grid-column: 1 / -1; }
.plan-field > span:first-child { color: var(--ui-foreground); font-size: .78rem; font-weight: 650; }
.plan-field input, .plan-file-control { background: var(--ui-background); border: 1px solid var(--ui-input); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); min-height: 2.75rem; padding: .65rem .75rem; width: 100%; }
.plan-money-input { align-items: center; display: flex; position: relative; }
.plan-money-input > span { color: var(--ui-muted-foreground); left: .75rem; position: absolute; }
.plan-money-input input { padding-left: 1.8rem; }
.plan-file-field > input { inset: 1.65rem 0 0; opacity: 0; position: absolute; z-index: 1; }
.plan-file-control { align-items: center; color: var(--ui-muted-foreground); display: flex; gap: .5rem; }
.plan-file-field small, .plan-error-box { color: var(--ui-destructive); font-size: .72rem; }
.plan-error-box { background: color-mix(in srgb, var(--ui-destructive) 8%, transparent); border-bottom: 1px solid var(--ui-border); padding: .8rem 1.2rem; }
.plan-form-actions { border-top: 1px solid var(--ui-border); display: flex; gap: .6rem; justify-content: flex-end; padding: 1rem 1.2rem; }
.plan-primary-button, .plan-outline-button { align-items: center; border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); cursor: pointer; display: inline-flex; font-size: .8rem; font-weight: 650; gap: .5rem; justify-content: center; min-height: 2.5rem; padding: 0 1rem; }
.plan-primary-button { background: var(--ui-primary); color: var(--ui-primary-foreground); }
.plan-outline-button { background: var(--ui-background); border-color: var(--ui-border); color: var(--ui-foreground); }
.plan-primary-button:disabled, .plan-outline-button:disabled { cursor: not-allowed; opacity: .5; }
@media (max-width: 800px) { .plan-grid { grid-template-columns: 1fr; } }
@media (max-width: 640px) { .plan-header { align-items: stretch; flex-direction: column; gap: 1rem; } .plan-form-grid { grid-template-columns: 1fr; } .plan-field-wide { grid-column: auto; } .plan-premiums { grid-template-columns: 1fr; } .plan-premiums > div + div { border-left: 0; border-top: 1px solid var(--ui-border); } .plan-benefit-row { grid-template-columns: 1.5rem 1fr; } .plan-benefit-row p { grid-column: 2; } .plan-form-actions { flex-direction: column-reverse; } .plan-primary-button, .plan-outline-button { width: 100%; } }
</style>
